import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWhiteLabel } from '../hooks/useWhiteLabel';
import toast from 'react-hot-toast';
import { dpsTheme } from '../styles/dpsTheme';
import { useDeviceSize } from '../hooks/useMediaQuery';
import { NotificationBell } from './notifications/NotificationBell';

function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout, userProfile } = useAuth();
  const { appName, logoUrl } = useWhiteLabel();
  const { isMobile } = useDeviceSize();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const isDPSPage = location.pathname.startsWith('/dps');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      setIsMenuOpen(false);
      navigate('/');
      toast.success('Déconnexion réussie');
    } catch (error) {
      toast.error('Erreur lors de la déconnexion');
    }
  };

  if (isDPSPage) {
    return null;
  }

  return (
    <nav
      style={{
        fontFamily: dpsTheme.typography.fontFamily,
        background: dpsTheme.colors.background.header,
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${dpsTheme.colors.border.primary}`,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
      }}
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '0 16px' : '0 48px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: isMobile ? '56px' : '64px' }}>
          <Link
            to="/"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '8px' : '12px',
              textDecoration: 'none',
              transition: 'opacity 0.2s',
              minWidth: 0,
              flex: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={appName}
                style={{ height: isMobile ? '32px' : '36px', width: 'auto', objectFit: 'contain', flexShrink: 0 }}
              />
            ) : (
              <div
                style={{
                  width: isMobile ? '32px' : '36px',
                  height: isMobile ? '32px' : '36px',
                  background: dpsTheme.colors.accent.blue.gradient,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <svg width={isMobile ? '18' : '20'} height={isMobile ? '18' : '20'} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
            )}
            {!isMobile && (
              <span style={{
                fontSize: '18px',
                fontWeight: 600,
                color: dpsTheme.colors.text.primary
              }}>
                {appName}
              </span>
            )}
          </Link>

          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
            {userProfile?.role !== 'client' && <Link
              to="/diagnostic"
              style={{
                padding: isMobile ? '8px 12px' : '10px 24px',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: isMobile ? '12px' : '14px',
                textDecoration: 'none',
                transition: 'all 0.2s',
                border: 'none',
                background: dpsTheme.colors.accent.blue.gradient,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? '4px' : '8px',
                boxShadow: dpsTheme.shadows.button,
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = dpsTheme.shadows.button;
              }}
            >
              <svg width={isMobile ? '14' : '16'} height={isMobile ? '14' : '16'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H7a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              {!isMobile && 'Lancer le diagnostic'}
            </Link>}

            {currentUser && userProfile?.role === 'advisor' && (
              <NotificationBell userId={currentUser.id} />
            )}

            {currentUser ? (
              <div style={{ position: 'relative' }} ref={menuRef}>
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  style={{
                    padding: isMobile ? '8px 12px' : '10px 20px',
                    borderRadius: '8px',
                    fontWeight: 500,
                    fontSize: isMobile ? '12px' : '14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: `1px solid ${dpsTheme.colors.border.primary}`,
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: dpsTheme.colors.text.secondary,
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '4px' : '8px',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  Mon compte
                  {!isMobile && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      style={{
                        transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  )}
                </button>

                {isMenuOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 8px)',
                      right: 0,
                      background: 'rgba(30, 41, 59, 0.9)',
                      backdropFilter: 'blur(20px)',
                      border: `1px solid ${dpsTheme.colors.border.primary}`,
                      borderRadius: '12px',
                      minWidth: '200px',
                      boxShadow: dpsTheme.shadows.hover,
                      overflow: 'hidden',
                    }}
                  >
                    <Link
                      to="/dashboard"
                      onClick={() => setIsMenuOpen(false)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        color: dpsTheme.colors.text.secondary,
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: 500,
                        transition: 'all 0.2s',
                        borderBottom: `1px solid ${dpsTheme.colors.border.secondary}`,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.color = dpsTheme.colors.text.primary;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = dpsTheme.colors.text.secondary;
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="7" height="7" />
                        <rect x="14" y="3" width="7" height="7" />
                        <rect x="14" y="14" width="7" height="7" />
                        <rect x="3" y="14" width="7" height="7" />
                      </svg>
                      Tableau de bord
                    </Link>

                    <Link
                      to="/account/settings"
                      onClick={() => setIsMenuOpen(false)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        color: dpsTheme.colors.text.secondary,
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: 500,
                        transition: 'all 0.2s',
                        borderBottom: `1px solid ${dpsTheme.colors.border.secondary}`,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.color = dpsTheme.colors.text.primary;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = dpsTheme.colors.text.secondary;
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="8" r="4" />
                        <path d="M12 14c-4 0-7 2-7 4v1h14v-1c0-2-3-4-7-4z" />
                      </svg>
                      Mon compte
                    </Link>

                    <button
                      onClick={handleLogout}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        color: '#f87171',
                        fontSize: '14px',
                        fontWeight: 500,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        textAlign: 'left',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                        e.currentTarget.style.color = '#fca5a5';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#f87171';
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      Déconnexion
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                style={{
                  padding: isMobile ? '8px 12px' : '10px 20px',
                  borderRadius: '8px',
                  fontWeight: 500,
                  fontSize: isMobile ? '12px' : '14px',
                  textDecoration: 'none',
                  transition: 'all 0.2s',
                  border: `1px solid ${dpsTheme.colors.border.primary}`,
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: dpsTheme.colors.text.secondary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? '4px' : '8px',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                }}
              >
                <svg width={isMobile ? '14' : '16'} height={isMobile ? '14' : '16'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                {!isMobile && 'Se connecter'}
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;